import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, Card, Space, Tag, Input, Select, Image, message, Popconfirm, Switch, Tooltip
} from 'antd';
import {
  EditOutlined, DeleteOutlined, SearchOutlined, ShopOutlined, DownloadOutlined, EyeOutlined, EyeInvisibleOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { useStores } from '../../stores';
import BackButton from '../../components/BackButton';

const { Search } = Input;

const ProductList = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { adminProductStore } = useStores();

  const [filters, setFilters] = useState({
    search: '',
    category: null,
    is_marketing_complete: null,
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      const apiFilters = {};
      if (filters.search) apiFilters.search = filters.search;
      if (filters.category) apiFilters.category = filters.category;
      if (filters.is_marketing_complete !== null) {
        apiFilters.is_marketing_complete = filters.is_marketing_complete;
      }

      await adminProductStore.fetchProducts(apiFilters);
    } catch (error) {
      message.error(t('admin.products.loadFailed'));
    }
  };

  const loadCategories = async () => {
    try {
      await adminProductStore.fetchCategories();
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDelete = async (id) => {
    try {
      await adminProductStore.deleteProduct(id);
      message.success(t('admin.products.deleteSuccess'));
    } catch (error) {
      // Show detailed error message from backend
      message.error(error.message || t('admin.products.deleteFailed'));
    }
  };

  const handleToggleVisibility = async (id, isActive) => {
    try {
      await adminProductStore.toggleProductVisibility(id, isActive);
      message.success(isActive ? t('admin.products.productVisible') : t('admin.products.productHidden'));
    } catch (error) {
      message.error(error.message || t('admin.products.toggleFailed'));
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.products.originalName'), dataIndex: 'name' },
      { title: t('admin.products.titleEn'), dataIndex: 'title_en' },
      { title: t('admin.products.titleEl'), dataIndex: 'title_el' },
      {
        title: t('admin.products.category'),
        dataIndex: 'category',
        exportRender: (cat) => cat?.name_en || ''
      },
      { title: t('admin.products.partnersCount'), dataIndex: 'partner_count' },
      { title: t('admin.products.variantsCount'), dataIndex: 'variant_count' },
      {
        title: t('admin.products.marketingStatus'),
        dataIndex: 'is_marketing_complete',
        exportRender: (val) => val ? t('admin.products.complete') : t('common.pending')
      }
    ];
    exportToExcel(adminProductStore.products, exportColumns, 'products', t('admin.products.title'));
    message.success(t('admin.products.exportSuccess'));
  };

  // Helper to get image URL from VersatileImageField
  const getImageUrl = (imageField) => {
    if (!imageField) return null;

    // VersatileImageField returns an object with sizes
    if (typeof imageField === 'object') {
      return imageField.thumbnail || imageField.medium || imageField.full_size;
    }

    // Fallback for old format (direct URL string)
    return imageField;
  };

  const columns = [
    {
      title: t('admin.products.image'),
      dataIndex: 'main_image',
      key: 'main_image',
      width: 60,
      fixed: 'left',
      render: (image) => {
        const imageUrl = getImageUrl(image);
        return imageUrl ? (
          <Image
            src={imageUrl}
            alt={t('admin.products.productAlt')}
            width={40}
            height={40}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div style={{ width: 40, height: 40, backgroundColor: '#f0f0f0' }} />
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('admin.products.originalName'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
      render: (text) => text || <em style={{ color: '#999' }}>{t('admin.products.notProvided')}</em>,
    },
    {
      title: t('admin.products.marketingTitle'),
      key: 'title',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><strong>EN:</strong> {record.title_en || <em style={{ color: '#999' }}>{t('admin.products.notSet')}</em>}</div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><strong>EL:</strong> {record.title_el || <em style={{ color: '#999' }}>{t('admin.products.notSet')}</em>}</div>
        </div>
      ),
    },
    {
      title: t('admin.products.category'),
      dataIndex: 'category',
      key: 'category',
      width: 120,
      ellipsis: true,
      render: (category) => category ? (
        <Tag color="blue">{i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en}</Tag>
      ) : (
        <Tag color="red">{t('admin.products.notSet')}</Tag>
      ),
    },
    {
      title: t('admin.products.partners'),
      dataIndex: 'partner_count',
      key: 'partner_count',
      width: 80,
      render: (count) => (
        <Space>
          <ShopOutlined />
          <span>{count || 0}</span>
        </Space>
      ),
    },
    {
      title: t('admin.products.variants'),
      dataIndex: 'variant_count',
      key: 'variant_count',
      width: 70,
      render: (count) => count || 0,
    },
    {
      title: t('admin.products.marketingStatus'),
      dataIndex: 'is_marketing_complete',
      key: 'is_marketing_complete',
      width: 100,
      render: (isComplete) => isComplete ? (
        <Tag color="green">{t('admin.products.complete')}</Tag>
      ) : (
        <Tag color="orange">{t('common.pending')}</Tag>
      ),
    },
    {
      title: t('admin.products.visibility'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (isActive, record) => (
        <Tooltip title={isActive ? t('admin.products.clickToHide') : t('admin.products.clickToShow')}>
          <Switch
            checked={isActive}
            onChange={(checked) => handleToggleVisibility(record.id, checked)}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
          />
        </Tooltip>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/products/enrich/${record.id}`)}
          >
            {t('admin.products.manage')}
          </Button>
          <Popconfirm
            title={t('admin.products.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            {t('admin.products.title')}
          </span>
        }
      >
        {/* Filters */}
        <Space style={{ marginBottom: 16 }} size="middle">
          <Search
            placeholder={t('admin.products.searchPlaceholder')}
            onSearch={handleSearch}
            onChange={(e) => !e.target.value && handleSearch('')}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />

          <Select
            placeholder={t('admin.products.filterByCategory')}
            style={{ width: 200 }}
            allowClear
            value={filters.category}
            onChange={(value) => handleFilterChange('category', value)}
          >
            {adminProductStore.categories.map(cat => (
              <Select.Option key={cat.id} value={cat.id}>
                {i18n.language === 'el' ? (cat.name_el || cat.name_en) : cat.name_en}
              </Select.Option>
            ))}
          </Select>

          <Select
            placeholder={t('admin.products.marketingStatus')}
            style={{ width: 180 }}
            allowClear
            value={filters.is_marketing_complete}
            onChange={(value) => handleFilterChange('is_marketing_complete', value)}
          >
            <Select.Option value={true}>{t('admin.products.complete')}</Select.Option>
            <Select.Option value={false}>{t('common.pending')}</Select.Option>
          </Select>

          <Button type="primary" onClick={loadProducts}>
            {t('admin.applyFilters')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!adminProductStore.products.length}
          >
            {t('admin.exportExcel')}
          </Button>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={adminProductStore.products}
          rowKey="id"
          loading={adminProductStore.loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </div>
  );
});

export default ProductList;
